import React, { useEffect, useRef } from 'react';
import Chart, { ArcElement } from 'chart.js/auto';
import { useThemeContext } from 'src/context/ThemeProvider';
import Color from 'color';
import { Box } from '@mui/material';

interface Props {
	chartValues: number[];
}

const DistributionChartForEligibleForPlacement = ({ chartValues }: Props) => {
	const chartInstanceRef = useRef<Chart | null>(null);
	const radialSkillScoreChartRef = useRef<HTMLCanvasElement>(null);
	const { themeMode } = useThemeContext();

	const getGraphColor = (label: string) => {
		switch (label) {
			case 'Eligible':
				return '#9FCBFA';
			case 'Not Eligible':
				return '#A99FF8';
		}
	};

	const chartLabels = ['Eligible', 'Not Eligible'];

	const generateChartColors = (context: any) => {
		const { chart } = context;
		const chartLabels = chart.data.labels || [];

		// Generate colors based on labels
		return chartLabels.map((label: string) => {
			const baseColor = Color(getGraphColor(label));
			return baseColor.toString(); // Apply a lighter shade for the gradient
		});
	};

	useEffect(() => {
		if (radialSkillScoreChartRef.current) {
			// Destroy the previous chart instance if it exists
			if (chartInstanceRef.current) {
				chartInstanceRef.current.destroy();
			}

			// Create a new chart instance
			const ctx = radialSkillScoreChartRef.current.getContext('2d');

			if (ctx) {
				chartInstanceRef.current = new Chart(ctx, {
					type: 'doughnut',
					data: {
						labels: chartLabels,
						datasets: [
							{
								data: chartValues,
								backgroundColor: generateChartColors,
								borderColor: themeMode === 'light' ? '#ffffff' : '#55555633',
								borderWidth: 2, // Reduced from 6 to 2
								borderRadius: 5, // Reduced from 10 to 5
								spacing: 4, // Added spacing between segments
							},
						],
					},
					options: {
						responsive: true,
						cutout: '70%',
						maintainAspectRatio: false,
						plugins: {
							legend: {
								display: true,
								position: 'right',
								labels: {
									font: {
										size: 12,
									},
									padding: 20,
									generateLabels: chart => {
										const datasets = chart.data.datasets;
										const labels = chart.data.labels;

										if (!datasets || !labels) return [];

										const total = datasets[0].data.reduce((sum, value) => (sum as number) + (value as number), 0);
										const meta = chart.getVisibleDatasetCount();

										return labels.map((label, index) => {
											const meta = chart.getDatasetMeta(index);
											const value = datasets[0].data[index] as number;
											const percentage = ((value / (total as number)) * 100).toFixed(1);
											return {
												text: `${label}: ${value} (${percentage}%)`,
												fillStyle: getGraphColor(label as string),
												strokeStyle: 'transparent', // Remove border
												lineWidth: 0, // Ensure no border
												index: index,
												datasetIndex: index,
											};
										});
									},
									usePointStyle: true, // Use point style for legend items
									pointStyle: 'circle', // Set point style to circle
								},
							},
							tooltip: {
								callbacks: {
									label: function (tooltipItem) {
										const labelIndex = tooltipItem.dataIndex;
										if (!tooltipItem?.chart?.data?.labels) return;
										const label = tooltipItem?.chart?.data?.labels[labelIndex];
										const value = tooltipItem.raw;

										// Return the correct label and value for the tooltip
										return `${label}: ${value}`;
									},
								},
							},
						},
						layout: {
							padding: {
								top: 20,
								right: 20,
								bottom: 20,
								left: 20,
							},
						},
						animation: {
							onComplete: function (animation) {
								const chart = animation.chart;
								const ctx = chart.ctx;
								const centerX = chart.chartArea.left + chart.chartArea.width / 2;
								const centerY = chart.chartArea.top + chart.chartArea.height / 2;

								ctx.save();
								ctx.font = 'bold 0.6rem Arial';
								ctx.textAlign = 'center';
								ctx.textBaseline = 'middle';
								chart.data.labels?.forEach((label, index) => {
									const dataPoint = chart.getDatasetMeta(0).data[index] as ArcElement;
									const angle = dataPoint.startAngle + (dataPoint.endAngle - dataPoint.startAngle) / 2;

									const chartRadius =
										Math.min(
											chart.chartArea.right - chart.chartArea.left,
											chart.chartArea.bottom - chart.chartArea.top,
										) / 2;

									const labelRadius = chartRadius * 1.06;

									const x = centerX + Math.cos(angle) * labelRadius;
									const y = centerY + Math.sin(angle) * labelRadius;

									ctx.save();
									ctx.translate(x, y);
									ctx.textAlign = x < centerX ? 'right' : 'left';
									ctx.textBaseline = 'middle';
									ctx.fillStyle = themeMode === 'light' ? '#000000' : '#FFFFFF';

									ctx.restore();
								});

								ctx.restore();
							},
						},
					},
				}) as Chart;
			}
		}

		// Cleanup function to destroy the chart when the component unmounts
		return () => {
			if (chartInstanceRef.current) {
				chartInstanceRef.current.destroy();
			}
		};
	}, [chartLabels]); // Updated dependency array

	return (
		<div className="w-full flex flex-col gap-8 md:flex-row justify-center">
			<div className="w-full flex flex-col gap-2 px-6 py-4 text-xs bg-white rounded-2xl text-zinc-900">
				<Box
					className="flex-shrink-0 text-sm font-semibold font-inter text-[#1C1C1C]"
					style={{ fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on" }}
				>
					Eligible Students for Placement Drive
				</Box>
				<div className="my-auto md:flex w-[100%] items-start justify-center">
					<canvas id="radialSkillScoreChart" className="!w-full h-[350px]" ref={radialSkillScoreChartRef}></canvas>
				</div>
			</div>
		</div>
	);
};

export default DistributionChartForEligibleForPlacement;
